import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import isEmpty from 'lodash/isEmpty';
import type { ReactElement } from 'react';
import { Fragment, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';

import type { AccountApiError } from '@/types/accountApiType';
import type { SalesforceLanguageCodeEnum } from '@/types/regionTypes';

import { Col } from '@/components/Col';
import { Flex } from '@/components/Flex';
import type { SelectOption } from '@/components/Select/Select';
import { Typography } from '@/components/Typography';
import { FixedPageDocument } from '@/graphql/contentful/api';
import type { CountryCode } from '@/graphql/saleor/api';
import { useNewsLetter } from '@/hooks/useNewsLetter';
import type { ContentfulFixedPageCMSData } from '@/integrations/contentful';
import {
  generateSectionKey,
  renderContentfulFixedPage,
} from '@/integrations/contentful';
import { Default } from '@/layouts/Default';
import { getLocaleForCountryAndLanguage } from '@/lib/regions';
import {
  NewsLetterSubSuccessDescUser,
  NewsLetterViewExclusiveUpdates,
  NewsLetterViewStayUpToDate,
} from '@/messages/newsletter';
import type { NewsLetterSchema } from '@/views/NewsLetterView/schema';

import { NewsLetterForm } from './NewsLetterForm';

import * as S from './NewsLetterView.styles';

export interface NewsLetterViewProps {
  cms?: ContentfulFixedPageCMSData;
}
export const NewsLetterView = ({ cms }: NewsLetterViewProps) => {
  const liveCms = useContentfulLiveUpdates(cms, {
    query: FixedPageDocument,
  });
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState<AccountApiError | undefined>(undefined);
  const { subscribeNewsLetterForGuest } = useNewsLetter();
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit: SubmitHandler<NewsLetterSchema> = async ({
    country,
    email,
    firstName,
    firstNameKanji,
    language,
    lastName,
    lastNameKanji,
    newsletterConsent,
    phone,
    phonePrefix,
    reCaptchaResponse,
    title,
  }: NewsLetterSchema) => {
    if (!newsletterConsent) return;
    const _country = country as SelectOption;
    const _language = language as SelectOption;
    const _title = title as SelectOption;
    const languageCode = _language.value as SalesforceLanguageCodeEnum;
    try {
      setLoading(true);
      const res = await subscribeNewsLetterForGuest({
        countryLanguageSlug: getLocaleForCountryAndLanguage(
          _country.value,
          languageCode,
        ),
        websiteCountryCode: _country.value as CountryCode,
        languageCode,
        title: _title.value,
        firstName,
        lastName,
        email,
        recaptchaResponse: reCaptchaResponse,
        ...(!!firstNameKanji && { firstNameKanji }),
        ...(!!lastNameKanji && { lastNameKanji }),
        ...(!!phone && { phone }),
        ...(!!phonePrefix?.value && { phonePrefix: phonePrefix?.value }),
      });
      setLoading(false);
      if (res.code === 200) {
        setIsSubscribed(true);
        setError(undefined);
      } else {
        setError({
          code: res.code,
          msg: res.msg ?? res.message ?? (res as any)?.detail?.[0]?.msg,
        });
      }
    } catch (error) {
      setLoading(false);
    }
  };
  return (
    <>
      {liveCms?.topContent?.map((data, i) => (
        <Fragment key={generateSectionKey(data, i)}>
          {renderContentfulFixedPage(
            data,
            undefined,
            liveCms.cmsExtraData,
            false,
            false,
          )}
        </Fragment>
      ))}
      <Flex alignItems='center' flexDirection='column'>
        {isSubscribed ? (
          <Typography
            data-testid='NewsLetterViewSubscribed'
            margin={6}
            textAlign='center'
          >
            {NewsLetterSubSuccessDescUser}
          </Typography>
        ) : (
          <Fragment>
            <S.Row gapDensity='none'>
              <Col>
                <Typography
                  data-testid='NewsLetterViewStayUpToDate'
                  mb={1}
                  textAlign='center'
                >
                  {NewsLetterViewStayUpToDate}
                </Typography>
              </Col>
            </S.Row>
            <S.Row gapDensity='none'>
              <Col>
                <Typography
                  data-testid='NewsLetterViewExclusiveUpdates'
                  mb={3}
                  textAlign='center'
                >
                  {NewsLetterViewExclusiveUpdates}
                </Typography>
              </Col>
            </S.Row>
            <S.FormWrap data-testid='NewsLetterViewNewsletterForm'>
              <NewsLetterForm
                handleSubmit={handleSubmit}
                isProcessing={loading}
                source='page'
              ></NewsLetterForm>
              {!isEmpty(error) && error.msg && (
                <Typography
                  color='error'
                  data-testid='NewsLetterViewErrorMessage'
                  scale='p3'
                  textAlign='center'
                >
                  {error.msg}
                </Typography>
              )}
            </S.FormWrap>
          </Fragment>
        )}
      </Flex>
    </>
  );
};

NewsLetterView.getLayout = (page: ReactElement) => {
  return <Default cmsData={page.props.cmsDefaultLayoutData}>{page}</Default>;
};
